import type { OrderDetailDTO } from "@/modules/orders/domain/contracts";
import React from "react";
import { createRoot } from "react-dom/client";
import { InvoicePrintView } from "../ui/print/InvoicePrintView";
import { ReceiptPrintView } from "../ui/print/ReceiptPrintView";
import { ShippingLabelPrintView } from "../ui/print/ShippingLabelPrintView";
import type { OrderPrintData } from "./print-types";
import { mapOrderDetailToPrintData } from "./print-types";

export type PrintType = "receipt" | "invoice" | "shipping-label";

export type PrintOrderOptions = {
  /** ຈາກຕັ້ງຄ່າທົ່ວໄປ → general.phone */
  contactPhone?: string | null;
};

function buildPrintData(
  order: OrderDetailDTO,
  options?: PrintOrderOptions,
): OrderPrintData {
  const base = mapOrderDetailToPrintData(order);
  const phone = options?.contactPhone?.trim();
  return {
    ...base,
    ...(phone ? { contactPhone: phone } : {}),
  };
}

// Common base styles
const baseStyles = `
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  .print-container {
    width: 100%;
    max-width: 100%;
    margin: 0;
    padding: 0;
  }

  .print-header {
    text-align: center;
    margin-bottom: 8px;
  }

  .brand-logo {
    margin-bottom: 4px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .logo-image {
    max-width: 25mm;
    max-height: 8mm;
    height: auto;
    width: auto;
    object-fit: contain;
  }

  .store-name {
    font-weight: bold;
    font-size: 12px;
    margin-bottom: 2px;
  }

  .document-type {
    font-weight: bold;
    font-size: 11px;
    text-transform: uppercase;
    margin-bottom: 2px;
  }

  .print-divider {
    border-top: 1px dashed #000;
    margin: 6px 0;
  }

  .print-section {
    margin: 6px 0;
    font-size: 10px;
    line-height: 1.4;
  }

  .section-title {
    font-weight: bold;
    margin-bottom: 4px;
  }

  .items-table {
    width: 100%;
    border-collapse: collapse;
    margin: 4px 0;
  }

  .items-table td {
    padding: 2px 0;
    font-size: 9px;
  }

  .items-table tr:last-child td {
    padding-bottom: 6px;
  }

  .total-row {
    display: flex;
    justify-content: space-between;
    margin: 3px 0;
    font-size: 10px;
  }

  .total-row.total {
    font-weight: bold;
    font-size: 11px;
    margin-top: 6px;
    padding-top: 6px;
    border-top: 1px solid #000;
  }

  .print-footer {
    text-align: center;
    margin-top: 10px;
    font-size: 9px;
    font-style: italic;
  }
`;

// Receipt styles - 72mm × 100mm per sheet (same media as shipping label)
const receiptStyles = `
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: "Courier New", Courier, monospace;
    font-size: 10px;
    line-height: 1.3;
    color: #000;
    background: #fff;
    width: 72mm;
    max-width: 72mm;
    min-height: 100mm;
    margin: 0 auto;
    padding: 2mm 2.5mm;
    box-sizing: border-box;
  }

  .print-container {
    width: 100%;
    max-width: 100%;
    margin: 0;
    padding: 0;
  }

  .print-header {
    margin-bottom: 4px;
  }

  .brand-header-row {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .brand-logo {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .logo-image {
    max-width: 15mm;
    max-height: 8mm;
    height: auto;
    width: auto;
    object-fit: contain;
  }

  .brand-text {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: start;
    align-items: start;
  }

  .brand-tagline {
    font-size: 8px;
    margin-bottom: 1px;
    color: #666;
    line-height: 1.2;
  }

  .brand-by {
    font-size: 9px;
    line-height: 1.2;
  }

  .contact-info {
    font-size: 9px;
    font-weight: bold;
  }

  .print-divider {
    border-top: 1px dashed #000;
    margin: 4px 0;
  }

  .print-section {
    margin: 4px 0;
    font-size: 10px;
    line-height: 1.4;
  }

  .section-title {
    font-weight: bold;
    margin-bottom: 2px;
  }

  .label-field {
    margin: 2px 0;
    font-size: 9px;
    display: flex;
    align-items: flex-start;
  }

  .label-text {
    font-weight: bold;
    min-width: 50px;
    margin-right: 4px;
  }

  .label-value {
    flex: 1;
    word-break: break-word;
  }

  .items-table {
    width: 100%;
    border-collapse: collapse;
    margin: 2px 0;
  }

  .items-table td {
    padding: 1px 0;
    font-size: 9px;
  }

  .items-table tr:last-child td {
    padding-bottom: 3px;
  }

  .total-row {
    display: flex;
    justify-content: space-between;
    margin: 2px 0;
    font-size: 10px;
  }

  .total-row.total {
    font-weight: bold;
    font-size: 11px;
    margin-top: 4px;
    padding-top: 4px;
    border-top: 1px solid #000;
  }

  .print-footer {
    text-align: center;
    margin-top: 6px;
    font-size: 8px;
    font-style: italic;
  }

  @media print {
    @page {
      size: 72mm 100mm;
      margin: 0;
    }

    .print-container.receipt {
      min-height: 100mm;
    }
  }
`;

// Invoice styles - A4 format
const invoiceStyles = `
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: "Arial", "Helvetica", sans-serif;
    font-size: 12px;
    line-height: 1.6;
    color: #000;
    background: #fff;
    margin: 0;
    padding: 20mm 15mm;
  }

  .print-container {
    max-width: 210mm;
    margin: 0 auto;
  }

  /* Header Section */
  .invoice-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 25px;
  }

  .invoice-header-left {
    display: flex;
    align-items: flex-start;
    gap: 20px;
  }

  .brand-logo {
    flex-shrink: 0;
  }

  .logo-image {
    max-width: 50mm;
    max-height: 18mm;
    height: auto;
    width: auto;
    object-fit: contain;
  }

  .company-info {
    display: flex;
    flex-direction: column;
  }

  .store-name {
    font-size: 24px;
    font-weight: bold;
    margin-bottom: 8px;
    color: #1a1a1a;
  }

  .company-details {
    font-size: 11px;
    color: #666;
    line-height: 1.8;
  }

  .invoice-header-right {
    text-align: right;
  }

  .document-type {
    font-size: 28px;
    font-weight: bold;
    text-transform: uppercase;
    margin-bottom: 15px;
    color: #1a1a1a;
    letter-spacing: 2px;
  }

  .invoice-meta {
    font-size: 11px;
  }

  .meta-row {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-bottom: 4px;
  }

  .meta-label {
    color: #666;
    min-width: 70px;
    text-align: right;
  }

  .meta-value {
    font-weight: 600;
    min-width: 100px;
    text-align: left;
  }

  /* Divider */
  .invoice-divider {
    border-top: 2px solid #000;
    margin: 20px 0;
  }

  /* Info Grid */
  .invoice-info-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 30px;
    margin-bottom: 25px;
  }

  .info-block {
    display: flex;
    flex-direction: column;
  }

  .info-block-title {
    font-size: 13px;
    font-weight: bold;
    margin-bottom: 10px;
    color: #1a1a1a;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .info-block-content {
    font-size: 12px;
    line-height: 1.8;
    color: #333;
  }

  .info-item {
    display: flex;
    gap: 10px;
    margin-bottom: 6px;
  }

  .info-label {
    font-weight: 600;
    min-width: 60px;
  }

  .info-value {
    color: #333;
  }

  /* Items Section */
  .invoice-items-section {
    margin-bottom: 25px;
  }

  .section-title {
    font-size: 16px;
    font-weight: bold;
    margin-bottom: 15px;
    color: #1a1a1a;
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  .invoice-items-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 10px;
  }

  .invoice-items-table thead {
    background-color: #f5f5f5;
  }

  .invoice-items-table th {
    padding: 12px 8px;
    text-align: left;
    font-size: 11px;
    font-weight: bold;
    text-transform: uppercase;
    color: #1a1a1a;
    border-bottom: 2px solid #000;
  }

  .invoice-items-table td {
    padding: 10px 8px;
    font-size: 12px;
    border-bottom: 1px solid #e0e0e0;
  }

  .invoice-items-table tbody tr:last-child td {
    border-bottom: 2px solid #000;
  }

  .col-item {
    width: 50%;
  }

  .col-qty {
    width: 10%;
    text-align: center;
  }

  .col-price {
    width: 20%;
    text-align: right;
  }

  .col-total {
    width: 20%;
    text-align: right;
    font-weight: 600;
  }

  .item-name {
    font-weight: 600;
    margin-bottom: 4px;
  }

  .item-variant {
    font-size: 11px;
    color: #666;
    margin-top: 2px;
  }

  /* Totals Section */
  .invoice-totals {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 30px;
  }

  .totals-table {
    width: 300px;
  }

  .total-row {
    display: flex;
    justify-content: space-between;
    padding: 8px 0;
    font-size: 12px;
    border-bottom: 1px solid #e0e0e0;
  }

  .total-label {
    color: #666;
  }

  .total-value {
    font-weight: 600;
    color: #1a1a1a;
  }

  .total-row-final {
    border-top: 2px solid #000;
    border-bottom: 2px solid #000;
    padding: 12px 0;
    margin-top: 5px;
    font-size: 16px;
    font-weight: bold;
  }

  .total-row-final .total-label {
    color: #1a1a1a;
    font-weight: bold;
  }

  .total-row-final .total-value {
    font-size: 18px;
  }

  /* Footer */
  .invoice-footer {
    margin-top: 40px;
    padding-top: 20px;
    border-top: 1px solid #e0e0e0;
    text-align: center;
  }

  .footer-text {
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 8px;
    color: #1a1a1a;
  }

  .footer-note {
    font-size: 10px;
    color: #666;
    font-style: italic;
  }

  @media print {
    @page {
      size: A4;
      margin: 0;
    }

    .print-container.invoice {
      page-break-inside: avoid;
    }

    .invoice-header,
    .invoice-info-grid,
    .invoice-items-section,
    .invoice-totals {
      page-break-inside: avoid;
    }
  }
`;

// Shipping label styles - 72mm x 100mm (thermal), up to 5 line items via view
const shippingLabelStyles = `
  ${baseStyles}

  body {
    font-family: "Courier New", Courier, monospace;
    font-size: 10px;
    line-height: 1.32;
    color: #000;
    background: #fff;
    width: 72mm;
    max-width: 72mm;
    min-height: 100mm;
    margin: 0 auto;
    /* padding stays 0 — page is exactly @page height; inset on .shipping-label avoids clipping the footer when printing */
    padding: 0;
    font-weight: 700;
    box-sizing: border-box;
  }

  .print-header {
    margin-bottom: 2px;
  }

  .brand-header-row {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .brand-logo {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 0;
  }

  .logo-image {
    max-width: 18mm;
    max-height: 6mm;
  }

  .brand-text {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: start;
    align-items: start;
  }

  .brand-tagline {
    font-size: 8px;
    margin-bottom: 0;
    color: #222;
    line-height: 1.18;
    font-weight: 700;
  }

  .brand-by {
    font-size: 9px;
    line-height: 1.18;
    font-weight: 800;
  }

  .contact-info {
    font-size: 9px;
    font-weight: 800;
  }

  .sender-line {
    white-space: nowrap;
    overflow-x: hidden;
    text-overflow: ellipsis;
    line-height: 1.35;
    padding: 0.08em 0;
  }

  /* ແຖວເບີຜູ້ສົ່ງ + Order: ຕົວເລັກລົງ ແລະ ຫໍ່ໄດ້ຫຼາຍບັນທັດ — ບໍ່ຕັດ … */
  .shipping-label-sender-strip {
    font-size: 9.5px;
    font-weight: 900;
    color: #000;
    letter-spacing: 0.01em;
    line-height: 1.32;
    white-space: normal;
    overflow-x: visible;
    text-overflow: clip;
    word-break: break-word;
    overflow-wrap: anywhere;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .label-field {
    margin: 1px 0;
    font-size: 9px;
    display: flex;
    align-items: flex-start;
    font-weight: 800;
  }

  .label-field-tight {
    margin-top: 2px;
  }

  .label-text {
    font-weight: 800;
    min-width: 54px;
    margin-right: 4px;
  }

  .label-value {
    flex: 1;
    word-break: break-word;
    font-weight: 800;
  }

  .label-destination-line {
    font-size: 9px;
    font-weight: 800;
    line-height: 1.25;
    word-break: break-word;
    margin-bottom: 1px;
  }

  .label-items-block {
    margin-top: 3px;
  }

  .label-items-overflow {
    font-size: 8px;
    font-weight: 800;
    margin-top: 2px;
    color: #222;
  }

  .label-items-title {
    font-size: 10px;
    font-weight: 800;
    margin-bottom: 3px;
  }

  .label-item {
    margin: 2px 0;
    padding-bottom: 2px;
    border-bottom: 1px dashed #333;
  }

  .label-item:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  .label-item-name {
    font-size: 9px;
    font-weight: 800;
    line-height: 1.5;
    padding: 0.12em 0;
    word-break: break-word;
    overflow-wrap: anywhere;
  }

  .label-item-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 3px;
    font-size: 8px;
    font-weight: 800;
    margin-top: 2px;
  }

  .label-total {
    margin-top: 3px;
    padding-top: 2px;
    border-top: 1px solid #000;
  }

  /* COD: ເດັ້ນກວ່າບອດກົງອື່ນເພື່ອອ່ານງ່າຍ */
  .shipping-label-luum-pay-thang {
    font-size: 12px;
    font-weight: 900;
    align-items: baseline;
    border-top-width: 2px;
    padding-top: 3px;
    margin-top: 4px;
    color: #000;
    letter-spacing: 0.02em;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .shipping-label-luum-pay-thang .label-text {
    font-weight: 900;
    font-size: inherit;
    min-width: fit-content;
  }

  .shipping-label-luum-pay-thang .label-value {
    font-weight: 900;
    font-size: inherit;
  }

  .order-info {
    font-size: 9px;
    font-weight: 800;
  }

  .print-footer {
    font-size: 8px;
    font-weight: 700;
    margin-top: 3px;
    font-style: normal;
  }

  .print-divider {
    margin: 3px 0;
  }

  .print-section {
    margin: 3px 0;
  }

  .print-container.shipping-label {
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    min-height: 100mm;
    height: 100mm;
    max-height: 100mm;
    padding: 2mm 2.5mm;
    overflow: hidden;
    page-break-inside: avoid;
  }

  /* flex-basis 0 so “main” only eats space left after the footer reserve; avoids clipping the footer bottom on print engines */
  .shipping-label-main {
    flex: 1 1 0;
    min-height: 0;
    overflow: hidden;
  }

  .shipping-label-footer-end {
    flex-shrink: 0;
    margin-top: auto;
  }

  .shipping-label-footer-end .print-footer {
    margin-top: 2px;
    font-size: 8px;
    font-weight: 700;
    font-style: normal;
    color: #000;
    text-align: center;
    print-color-adjust: exact;
    -webkit-print-color-adjust: exact;
  }

  @media print {
    @page {
      size: 72mm 100mm;
      margin: 0;
    }
  }
`;

/**
 * Open print dialog with React component
 */
function printWithComponent(
  data: OrderPrintData,
  Component: React.ComponentType<{ order: OrderPrintData }>,
  title: string,
  styles: string,
) {
  if (!data) return;

  const printWindow = window.open("", "_blank");
  if (!printWindow) return;

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>${title} - ${data.orderNumber}</title>
        <style>${styles}</style>
      </head>
      <body>
        <div id="print-root"></div>
      </body>
    </html>
  `);

  printWindow.document.close();

  // Wait for DOM to be ready
  setTimeout(() => {
    const rootElement = printWindow.document.getElementById("print-root");
    if (!rootElement) return;

    const root = createRoot(rootElement);
    root.render(React.createElement(Component, { order: data }));

    // Wait for render then print — register afterprint before print() for reliable auto-close
    setTimeout(() => {
      let fallbackId: ReturnType<typeof window.setTimeout> | undefined;
      let done = false;
      const closeOnce = () => {
        if (done) return;
        done = true;
        if (fallbackId !== undefined) window.clearTimeout(fallbackId);
        try {
          root.unmount();
        } catch {
          /* noop */
        }
        if (!printWindow.closed) printWindow.close();
      };

      printWindow.addEventListener("afterprint", closeOnce, { once: true });

      printWindow.focus();
      printWindow.print();

      // Some environments never fire afterprint; avoid leaving a blank tab forever
      fallbackId = window.setTimeout(closeOnce, 60_000);
    }, 100);
  }, 100);
}

/**
 * Open print dialog for receipt
 */
export function printReceipt(
  order: OrderDetailDTO,
  options?: PrintOrderOptions,
) {
  printWithComponent(
    buildPrintData(order, options),
    ReceiptPrintView,
    "Receipt",
    receiptStyles,
  );
}

/**
 * Open print dialog for invoice
 */
export function printInvoice(
  order: OrderDetailDTO,
  options?: PrintOrderOptions,
) {
  printWithComponent(
    buildPrintData(order, options),
    InvoicePrintView,
    "Invoice",
    invoiceStyles,
  );
}

/**
 * Open print dialog for shipping label
 */
export function printShippingLabel(
  order: OrderDetailDTO,
  options?: PrintOrderOptions,
) {
  printWithComponent(
    buildPrintData(order, options),
    ShippingLabelPrintView,
    "Shipping Label",
    shippingLabelStyles,
  );
}

/**
 * Generic print function that routes to the appropriate print function
 */
export function printOrder(
  order: OrderDetailDTO,
  type: PrintType,
  options?: PrintOrderOptions,
) {
  switch (type) {
    case "receipt":
      printReceipt(order, options);
      break;
    case "invoice":
      printInvoice(order, options);
      break;
    case "shipping-label":
      printShippingLabel(order, options);
      break;
    default:
      console.error("Unknown print type:", type);
  }
}
